// Groups follow the `<app>:<role>` convention. This module turns the flat list of
// Cognito group names into per-application sections for the admin UI.

/** Friendly labels for app prefixes. Unlisted prefixes fall back to Title Case. */
const APP_LABELS: Record<string, string> = {
  auth: 'User admin',
  dash: 'Dash',
  sms: 'SMS',
  wiki: 'Wiki',
  mapsly: 'Mapsly',
  calendly: 'Scheduler',
  offer: 'Offer',
  portfolio: 'Portfolio',
  cam: 'CAM',
  utils: 'Utils',
  redzaps: 'Redzaps',
  gplpmatch: 'GPLP Match',
  crm: 'CRM',
  docsend: 'Deals',
  lease: 'Lease',
  ingest: 'Ingest',
};

/** Roles are shown most-powerful first; unknown roles sort after these, alphabetically. */
const ROLE_ORDER = ['admin', 'editor', 'user', 'viewer'];

export interface GroupOption {
  /** Full Cognito group name, e.g. `dash:editor`. */
  name: string;
  /** Role part, e.g. `editor`. */
  role: string;
  description?: string;
}

export interface AppSection {
  app: string;
  label: string;
  groups: GroupOption[];
}

export function appLabel(app: string): string {
  return APP_LABELS[app] ?? app.charAt(0).toUpperCase() + app.slice(1);
}

export function splitGroup(name: string): { app: string; role: string } {
  const i = name.indexOf(':');
  if (i === -1) return { app: name, role: '' };
  return { app: name.slice(0, i), role: name.slice(i + 1) };
}

function roleRank(role: string): number {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
}

/** Group a flat list of Cognito groups into app sections, sorted by app label then role. */
export function groupByApp(groups: { name: string; description?: string }[]): AppSection[] {
  const byApp = new Map<string, AppSection>();
  for (const g of groups) {
    const { app, role } = splitGroup(g.name);
    let section = byApp.get(app);
    if (!section) {
      section = { app, label: appLabel(app), groups: [] };
      byApp.set(app, section);
    }
    section.groups.push({ name: g.name, role, description: g.description });
  }
  const sections = [...byApp.values()];
  for (const s of sections) {
    s.groups.sort((a, b) => roleRank(a.role) - roleRank(b.role) || a.role.localeCompare(b.role));
  }
  // "User admin" (auth:*) goes last; everything else alphabetical by label.
  sections.sort((a, b) => {
    if (a.app === 'auth') return 1;
    if (b.app === 'auth') return -1;
    return a.label.localeCompare(b.label);
  });
  return sections;
}

/** Summarise a user's memberships as `[{label, roles}]`, in the same order as groupByApp. */
export function summarizeMemberships(userGroups: string[]): { app: string; label: string; roles: string[] }[] {
  return groupByApp(userGroups.map((name) => ({ name }))).map((s) => ({
    app: s.app,
    label: s.label,
    roles: s.groups.map((g) => g.role),
  }));
}
